// src/routes/AppRoutes.jsx
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Loader from "../components/common/Loader.jsx";

// ── Carga inmediata: flujo público inicial ────────────────────────────────────
// Estas páginas deben estar disponibles en el bundle inicial para que la
// primera carga del login no tenga ningún delay.
import Login from "../pages/Login.js";
import Register from "../pages/Register.js";
import ForgotPassword from "../pages/ForgotPassword.js";
import ResetPassword from "../pages/ResetPassword.js";
import SetPassword from "../pages/SetPassword.js";
import VerifyEmail from "../pages/VerifyEmail.js";

// ── Rutas y layouts (también eager: son pequeños y necesarios para todo) ──────
import ProtectedRoute from "../ProtectedRoute.js";
import PrivateRouteAdmin from "./PrivateRouteAdmin.js";
import PrivateRouteOrgAdmin from "./PrivateRouteOrgAdmin.js";
import SidebarLayout from "../layouts/SidebarLayout.jsx";
import PublicLayout from "../layouts/PublicLayout.jsx";

// ── Carga diferida: páginas pesadas ───────────────────────────────────────────
// Cada página genera su propio chunk; se descarga solo cuando el usuario
// navega a esa ruta por primera vez.
const Status           = lazy(() => import("../pages/Status"));          // recharts
const Results          = lazy(() => import("../pages/Results.js"));       // chart.js + react-chartjs-2
const Benchmark        = lazy(() => import("../pages/Benchmark.jsx"));    // SVG charts
const Contacts         = lazy(() => import("../pages/Contacts.js"));
const CommunicationPanel = lazy(() => import("../pages/CommunicationPanel.js"));
const Instrumento      = lazy(() => import("../pages/Instrumento.js"));
const NuevoInstrumento = lazy(() => import("../pages/NuevoInstrumento.js"));
const Users            = lazy(() => import("../pages/Users.js"));
const AdminServiciosPage = lazy(() => import("../pages/AdminServiciosPage.js"));
const ServiciosAdmin   = lazy(() => import("../pages/ServiciosAdmin.js"));
const PreguntasAdmin   = lazy(() => import("../pages/PreguntasAdmin.js"));
const ApiCallsUsage    = lazy(() => import("../pages/ApiCallsUsage.jsx"));
const OrganizationSelector = lazy(() => import("../pages/OrganizationSelector.js"));
const Inicio           = lazy(() => import("../pages/Inicio.js"));
const Perfil           = lazy(() => import("../pages/Perfil.js"));
const Tutoriales       = lazy(() => import("../pages/Tutoriales.js"));

function AppRoutes() {
  return (
    // Un único Suspense envuelve todas las rutas lazy.
    // El Loader aparece solo durante la descarga del chunk de cada página.
    <Suspense fallback={<Loader text="Cargando..." />}>
      <Routes>
        {/* Redirección automática de la raíz a login */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* ── Rutas públicas (eager) ─────────────────────────────────────── */}
        <Route path="/login"           element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="/register"        element={<PublicLayout><Register /></PublicLayout>} />
        <Route path="/verify-email"    element={<PublicLayout><VerifyEmail /></PublicLayout>} />
        <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
        <Route path="/reset-password"  element={<PublicLayout><ResetPassword /></PublicLayout>} />
        <Route path="/set-password"    element={<PublicLayout><SetPassword /></PublicLayout>} />

        {/* ── Rutas protegidas (lazy) ────────────────────────────────────── */}
        <Route path="/inicio" element={
          <ProtectedRoute><SidebarLayout><Inicio /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/perfil" element={
          <ProtectedRoute><SidebarLayout><Perfil /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/status" element={
          <ProtectedRoute><SidebarLayout><Status /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/results" element={
          <ProtectedRoute><SidebarLayout><Results /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/contacts" element={
          <ProtectedRoute><SidebarLayout><Contacts /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/communication" element={
          <ProtectedRoute><SidebarLayout><CommunicationPanel /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/survey" element={
          <ProtectedRoute><SidebarLayout><Instrumento /></SidebarLayout></ProtectedRoute>
        } />
        <Route path="/survey/new" element={
          <ProtectedRoute><PrivateRouteOrgAdmin><SidebarLayout><NuevoInstrumento /></SidebarLayout></PrivateRouteOrgAdmin></ProtectedRoute>
        } />
        <Route path="/survey/update/:correlativo" element={
          <ProtectedRoute><PrivateRouteOrgAdmin><SidebarLayout><NuevoInstrumento mode="edit" /></SidebarLayout></PrivateRouteOrgAdmin></ProtectedRoute>
        } />
        <Route path="/benchmark" element={
          <ProtectedRoute><SidebarLayout><Benchmark /></SidebarLayout></ProtectedRoute>
        } />

        {/* ── Rutas con permisos de OrgAdmin ────────────────────────────── */}
        <Route path="/users" element={
          <ProtectedRoute><PrivateRouteOrgAdmin><SidebarLayout><Users /></SidebarLayout></PrivateRouteOrgAdmin></ProtectedRoute>
        } />
        <Route path="/usuarios" element={
          <ProtectedRoute><PrivateRouteOrgAdmin><SidebarLayout><Users /></SidebarLayout></PrivateRouteOrgAdmin></ProtectedRoute>
        } />

        {/* ── Rutas de admin ────────────────────────────────────────────── */}
        <Route path="/orgselector" element={
          <ProtectedRoute><PrivateRouteAdmin><PublicLayout><OrganizationSelector /></PublicLayout></PrivateRouteAdmin></ProtectedRoute>
        } />
        <Route path="/admin/servicios" element={
          <ProtectedRoute><PrivateRouteAdmin><SidebarLayout><AdminServiciosPage /></SidebarLayout></PrivateRouteAdmin></ProtectedRoute>
        } />
        <Route path="/admin/services" element={
          <ProtectedRoute><PrivateRouteAdmin><SidebarLayout><ServiciosAdmin /></SidebarLayout></PrivateRouteAdmin></ProtectedRoute>
        } />
        <Route path="/admin/questions" element={
          <ProtectedRoute><PrivateRouteAdmin><SidebarLayout><PreguntasAdmin /></SidebarLayout></PrivateRouteAdmin></ProtectedRoute>
        } />
        <Route path="/admin/api-calls" element={
          <ProtectedRoute><PrivateRouteAdmin><SidebarLayout><ApiCallsUsage /></SidebarLayout></PrivateRouteAdmin></ProtectedRoute>
        } />
        <Route path="/tutoriales" element={
          <ProtectedRoute><PrivateRouteAdmin><SidebarLayout><Tutoriales /></SidebarLayout></PrivateRouteAdmin></ProtectedRoute>
        } />

        {/* ── Aliases heredados ─────────────────────────────────────────── */}
        <Route path="/instrumento"          element={<Navigate to="/survey" replace />} />
        <Route path="/instrumento/nuevo"    element={<Navigate to="/survey/new" replace />} />
        <Route path="/new" element={
          <ProtectedRoute><SidebarLayout><NuevoInstrumento /></SidebarLayout></ProtectedRoute>
        } />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
